<script lang="ts">
    import Card from "$/components/card/Card.svelte";
    import { CopilotService } from "$/services/CopilotService";
    import { TokenStore } from "$/stores/TokenStore";
    import { fly } from "svelte/transition";
    import { flip } from "svelte/animate";
    import AppBar from "$/components/AppBar.svelte";
    import { formattedDateAndTime } from "$/utils/date";
    import CopilotLoadingGif from "$/assets/images/copilot-loading.gif";
    import { redirect } from "@roxi/routify";
    import { clickAction } from "$/utils/clickAction";
    import SidePanel from "$/components/SidePanel.svelte";
    import Settings from "$/components/Settings.svelte";
    import { AppStore, Panels } from "$/stores/AppStore";
    import UserSettingsPanel from "$/components/panels/UserSettingsPanel/UserSettingsPanel.svelte";

    let researchTopic: string = "";
    let researchDate: Date = new Date();

    let loading = false;

    let showSaveButton = false;
    let showAddSnippetsButton = false;

    let savingTopic = false;
    let addingSnippets = false;

    $: snippets = [];
    let retrievedSnippets = [];
    let lastAddedSnippetIndex = 0;

    const delay = (ms) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
    };

    const onInputKeyPress = async (event) => {
        if (event.key === "Enter") {
            await createResearchJob();
        }
    };

    const createResearchJob = async () => {
        loading = true;
        showSaveButton = false;

        let research = await CopilotService.createQuery({
            query: researchTopic,
        });

        researchDate = new Date();

        retrievedSnippets = research.snippets ?? [];

        await addSnippets(10);

        await delay(1500);
        showSaveButton = true;
        showAddSnippetsButton = true;
    };

    const addSnippets = async (count) => {
        if (addingSnippets) return;

        addingSnippets = true;

        let fromIndex = lastAddedSnippetIndex;
        let toIndex =
            fromIndex + count > retrievedSnippets.length
                ? retrievedSnippets.length
                : fromIndex + count;

        if (fromIndex === toIndex) {
            return;
        }

        for (let i = fromIndex; i < toIndex; i++) {
            await delay(1500);

            loading = false;

            let snippet = retrievedSnippets[i];
            snippet.createdOn = new Date();
            snippet.copilotReactionValue = 0;

            snippets = [...snippets, snippet];

            AppStore.setFocused({ snippetId: snippet.id });
        }

        lastAddedSnippetIndex = toIndex;

        addingSnippets = false;

        if (toIndex === retrievedSnippets.length) {
            showAddSnippetsButton = false;
        }
    };

    const addCopilotReaction = async (reaction, snippet) => {
        // remove snippet if reaction is -1
        if (reaction === -1) {
            snippets = snippets.filter((s) => s.id !== snippet.id);
        }

        snippets = snippets.map((s) => {
            if (s.id === snippet.id) {
                // @ts-ignore
                s.copilotReactionValue = reaction;
            }
            return s;
        });

        await CopilotService.createReaction(
            reaction,
            snippet.id,
            snippet.copilotQueryId,
        );
    };

    const saveResearch = async () => {
        if (savingTopic) {
            return;
        }

        savingTopic = true;

        let research = {
            query: researchTopic,
            snippets: snippets.map((s) => {
                return {
                    id: s.id,
                    nameSuggestion: s.nameSuggestion,
                    url: s.urlPath,
                    content: s.content,
                };
            }),
        };

        let topic = await CopilotService.createTopic(research);

        $redirect(`/${topic.id}`);

        savingTopic = false;
    };
</script>

<div class="flex h-screen flex-col">
    <AppBar showMenuIcon={false} />

    <div class="page_wrapper">
        <SidePanel show={$AppStore.panel === Panels.SETTINGS}>
            <Settings standalone={true} on:close={AppStore.closePanels} />
        </SidePanel>

        <SidePanel show={$AppStore.panel === Panels.USER_SETTINGS}>
            <UserSettingsPanel
                on:close={AppStore.closePanels}
            ></UserSettingsPanel>
        </SidePanel>

        <div class="z-0 flex w-full flex-1 flex-col">
            <!-- Show Loading -->
            {#if loading === true}
                <img class="loading" src={CopilotLoadingGif} alt="Loading..." />

                <!-- Show Search Bar -->
            {:else if snippets.length === 0}
                <div class="m-auto flex max-w-md flex-col gap-4 p-4">
                    <h1 class="text-center text-2xl">
                        What shall we <span class="bg-yellow-300 px-1 font-bold"
                            >research</span
                        > today? 👀
                    </h1>

                    <div
                        class="flex w-full flex-row items-center gap-2 rounded-full border border-gray-800 py-2 pl-4 pr-2 text-xl"
                    >
                        <svg
                            class="h-6 w-6"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                            />
                        </svg>
                        <input
                            class="w-full flex-1 outline-none"
                            name="search"
                            type="text"
                            placeholder="Content Marketing Market Size"
                            bind:value={researchTopic}
                            on:keypress={onInputKeyPress}
                        />

                        {#if researchTopic.length > 0}
                            <div
                                class="copilot-input-search-icon"
                                use:clickAction={createResearchJob}
                            >
                                <svg
                                    class="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                                    />
                                </svg>
                            </div>
                        {/if}
                    </div>
                    {#if researchTopic.length > 0}
                        <span class="pr-4 text-right text-sm text-gray-500"
                            >Press <span
                                class="rounded-sm bg-gray-200 px-1 py-0.5 text-gray-500"
                                >⏎ Enter</span
                            > to start</span
                        >
                    {/if}
                </div>

                <!-- Show Resaerch Topic -->
            {:else}
                <div class="flex flex-col p-4">
                    <div class="text-xl font-medium">{researchTopic}</div>
                    <div class="text-xs">
                        {formattedDateAndTime(researchDate)}
                    </div>
                </div>
                <div
                    class="flex w-full flex-1 flex-col gap-4 px-4 pb-40 sm:overflow-y-hidden md:flex-row md:pb-4"
                >
                    {#each snippets as snippet (snippet.id)}
                        <div
                            class="w-full flex-auto flex-shrink-0 flex-grow-0 md:w-96"
                            animate:flip
                            in:fly={{ y: 200, duration: 2000 }}
                        >
                            <Card
                                isExpanded={true}
                                {addCopilotReaction}
                                isCopilot={true}
                                card={snippet}
                                editRole={0}
                                openInNewTab={true}
                                topicId={null}
                                user={$TokenStore?.user}
                            ></Card>
                        </div>
                    {/each}
                </div>
            {/if}

            {#if showSaveButton}
                <div
                    in:fly={{ y: 200, duration: 2000 }}
                    class="white-shadow fixed bottom-0 z-50 flex w-full flex-row flex-wrap items-center justify-center gap-x-4 gap-y-0 bg-white p-4 md:bottom-4 md:bg-transparent"
                >
                    <button
                        on:click={saveResearch}
                        class="mb-2 flex flex-row items-center gap-1 rounded-full bg-gray-700 px-4 py-2 text-lg font-light text-white transition-all hover:scale-110 hover:bg-gray-900"
                        class:text-gray-300={savingTopic}
                    >
                        {#if savingTopic}
                            <svg
                                class="mr-1 h-5 w-5 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                                />
                            </svg>
                            Saving Topic...
                        {:else}
                            <svg
                                class="mr-1 h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"
                                />
                            </svg>
                            Save <b class="font-black">Research</b> Topic
                        {/if}
                    </button>

                    <button
                        on:click={() => {
                            addSnippets(5);
                        }}
                        class="mb-2 flex flex-row items-center gap-1 rounded-full bg-gradient-to-r from-orange-600 via-purple-600 to-cyan-600 px-4 py-2 text-lg font-light text-white transition-all hover:scale-110"
                        class:hidden={!showAddSnippetsButton}
                    >
                        {#if addingSnippets}
                            <svg
                                class="mr-1 h-5 w-5 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            </svg>
                            <b class="font-black">Searching</b> for more...
                        {:else}
                            <svg
                                class="mr-1 h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                                />
                            </svg>
                            Find more <b class="font-black">Snippets</b>
                        {/if}
                    </button>
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    @import "../../main.css";

    .loading {
        @apply m-auto w-full max-w-xs hue-rotate-[290deg];
    }

    .white-shadow {
        @apply shadow-[0px_-20px_20px_0px_rgba(255,255,255,1)];
    }

    .copilot-input-search-icon {
        @apply flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-pink-500 text-white transition-all hover:scale-110;
    }

    .page_wrapper {
        @apply relative z-10 flex flex-grow overflow-y-auto;
    }
</style>
